/**
 * KK Favorites — wishlist saved in this browser (no login needed).
 *
 * Load with {% render 'kk-commerce-scripts' %}.
 *
 * Declarative (event delegation, works for markup added later):
 *   <button data-kk-favorite data-product-handle="herb-crunch" aria-pressed="false"
 *           data-label-add="Add Herb Crunch to favorites" data-label-remove="Remove Herb Crunch from favorites">
 *     → toggles; every button for the same product gets aria-pressed + data-active kept in sync.
 *   <span data-kk-favorites-count></span>   → number of favorites (data-count attribute too, for styling 0)
 *
 * Events on document: kk:favorites:updated { handles }
 * JS API: window.KKFavorites.list(), .has(handle), .toggle(handle), .remove(handle), .clear(),
 *         .addAllToCart() → { added, skipped }
 */
const STORAGE_KEY = 'kk:favorites';
const ROOT = window.Shopify?.routes?.root || '/';

class KKFavoritesStore {
  /** @type {string[]} */
  #handles = [];

  constructor() {
    this.#handles = this.#read();

    document.addEventListener('click', (event) => this.#onClick(event));

    // Other tabs changed the list.
    window.addEventListener('storage', (event) => {
      if (event.key !== STORAGE_KEY) return;
      this.#handles = this.#read();
      this.#sync();
    });

    // Cards rendered later (sections re-rendered, favorites page, editor) get the right state.
    // Batched to one check per frame.
    let frame = 0;
    new MutationObserver((mutations) => {
      if (frame || !mutations.some((m) => [...m.addedNodes].some((n) => n instanceof Element))) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        this.#sync();
      });
    }).observe(document.documentElement, { childList: true, subtree: true });

    this.#sync();
  }

  list() {
    return [...this.#handles];
  }

  has(handle) {
    return this.#handles.includes(handle);
  }

  toggle(handle) {
    if (!handle) return false;
    const next = !this.has(handle);
    this.#handles = next ? [handle, ...this.#handles] : this.#handles.filter((h) => h !== handle);
    this.#save();
    return next;
  }

  remove(handle) {
    if (!this.has(handle)) return;
    this.#handles = this.#handles.filter((h) => h !== handle);
    this.#save();
  }

  clear() {
    this.#handles = [];
    this.#save();
  }

  /**
   * Add the first available variant of every favorite to the cart (needs kk-cart.js).
   * @returns {Promise<{ added: number, skipped: number }>}
   */
  async addAllToCart() {
    const products = await Promise.all(
      this.#handles.map((handle) =>
        fetch(`${ROOT}products/${encodeURIComponent(handle)}.js`)
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      )
    );

    const items = products
      .map((product) => product?.variants?.find((variant) => variant.available))
      .filter(Boolean)
      .map((variant) => ({ id: variant.id, quantity: 1 }));

    if (items.length && window.KKCart) await window.KKCart.add(items);
    return { added: items.length, skipped: this.#handles.length - items.length };
  }

  // ---------------------------------------------------------------- internals

  #read() {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value) ? value.filter((h) => typeof h === 'string') : [];
    } catch {
      return [];
    }
  }

  #save() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.#handles));
    } catch {
      // Storage blocked (private mode): favorites still work until the page is left.
    }
    this.#sync();
    document.dispatchEvent(new CustomEvent('kk:favorites:updated', { detail: { handles: this.list() } }));
  }

  #sync() {
    document.querySelectorAll('[data-kk-favorite][data-product-handle]').forEach((button) => {
      const active = this.has(button.dataset.productHandle);
      if (button.getAttribute('aria-pressed') === String(active)) return;

      button.setAttribute('aria-pressed', String(active));
      button.toggleAttribute('data-active', active);
      const label = active ? button.dataset.labelRemove : button.dataset.labelAdd;
      if (label) button.setAttribute('aria-label', label);
    });

    const count = String(this.#handles.length);
    document.querySelectorAll('[data-kk-favorites-count]').forEach((el) => {
      if (el.textContent !== count) el.textContent = count;
      el.dataset.count = count;
    });
  }

  #onClick(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-kk-favorite]') : null;
    if (!button) return;
    event.preventDefault();
    this.toggle(button.dataset.productHandle);
  }
}

if (!window.KKFavorites) {
  window.KKFavorites = new KKFavoritesStore();
}
